import { valibotResolver } from "@hookform/resolvers/valibot";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import Skeleton from "common/components/ui/Skeleton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAgencySettings from "../../datahooks/useAgencySettings";
import useAgencySettingsMutations from "../../datahooks/useAgencySettingsMutations";
import { GTMIdSchema, type GTMIdSchemaType } from "../../schema";
import SettingsItemContainer from "../SettingsItemContainer";

export default function GTM() {
  const isDesktop = useTwBreakpoint("lg");
  const [isEditing, setIsEditing] = useState(false);
  const { agencySettings, isLoadingAgencySettings } = useAgencySettings();
  const { updateAgencySettings, isUpdatingAgencySettings } =
    useAgencySettingsMutations();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { gtm: agencySettings ? agencySettings.gtm : "" },
    resolver: valibotResolver(GTMIdSchema),
  });

  useEffect(() => {
    if (agencySettings) {
      reset({ gtm: agencySettings.gtm });
    }
  }, [agencySettings]);

  function onSubmit({ gtm }: GTMIdSchemaType) {
    updateAgencySettings({ updates: { gtm } }).then(() => {
      setIsEditing(false);
    });
  }
  function onCancel() {
    setIsEditing(false);
    reset({ gtm: agencySettings.gtm });
  }

  return (
    <SettingsItemContainer className="items-center">
      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <FormField
            label="Google tag manager ID"
            htmlFor="gtm-id"
            className="w-full"
            error={errors.gtm?.message}
            size={isDesktop ? "sm" : "lg"}
          >
            <div className="gap-x-2 gap-y-4 flex flex-col lg:flex-row">
              <Input
                id="gtm-id"
                placeholder="Enter GTM ID"
                variant={isDesktop ? "sm" : "lg"}
                className="lg:w-80"
                autoFocus
                {...register("gtm")}
              />
              <div className="flex gap-x-2 mt-auto ml-auto">
                <Button
                  variant="secondary-black"
                  size={isDesktop ? "sm" : "md"}
                  onClick={onCancel}
                  disabled={isUpdatingAgencySettings}
                >
                  Cancel
                </Button>
                <Button
                  size={isDesktop ? "sm" : "md"}
                  type="submit"
                  variant="secondary-purple"
                  isLoading={isUpdatingAgencySettings}
                >
                  Save
                </Button>
              </div>
            </div>
          </FormField>
        </form>
      ) : (
        <>
          <div className="flex flex-col gap-y-0.5">
            <span className="text-button-sm">Google tag manager ID</span>
            {isLoadingAgencySettings ? (
              <Skeleton />
            ) : (
              <span className="text-black-500 text-xs-medium">
                {agencySettings.gtm || "No GTM ID set"}
              </span>
            )}
          </div>
          <Button
            variant="ghost-black"
            intent="iconOnly"
            className="ml-auto"
            onClick={() => setIsEditing(true)}
          >
            <PencilSimpleLine />
          </Button>
        </>
      )}
    </SettingsItemContainer>
  );
}
